// Extracted from original/src/components/CashTransfers.tsx:2368-2640 — the three warning dialogs of the transfer form
// (ADR-08 §2.1 "copy, then cut logic"):
//   insufficient trust ("رصيد الأمانة غير كافٍ")               :2368-2456
//   partner-office debt overflow ("رصيد المكتب غير كافٍ")      :2458-2544
//   insufficient vault cash ("رصيد القاصة غير كافٍ للحوالة")   :2546-2640
// Kept: every class string, emoji, icon and the button order.
// Cut: `insufficientDetails` / `officeWarningDetails` / `vaultWarningDetails` (client-side checks over stored balances)
// → the figures of the server's refusal (`BALANCE_INSUFFICIENT` / `SURPLUS_ACCOUNT_REQUIRED` params, D-014, D-084),
// preformatted; `executeSubmit(true)` / `setPaymentType(…)` / the field resets → callbacks.
// Changed by decision: the overflow dialog also serves an incoming transfer that charges a partner beyond its trust
// (D-082: the rest is recorded as the partner's debt) with mirrored wording (V-33); the vault dialog opens only when
// cash actually leaves (D05 T-17).
// Added (new dialogs in the same visual vocabulary): the duplicate-code warning of an incoming transfer (D-087, V-37)
// and the cancel dialog with its mandatory reason (D-086, V-36). data-testid attributes.
import { useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { AlertTriangle } from 'lucide-react';
import { useI18n } from '../i18n-context.js';

export interface TrustWarningProps {
  readonly open: boolean;
  readonly clientName: string;
  readonly availableLabel: string;
  readonly requiredLabel: string;
  readonly onChooseOther: () => void;
  readonly onCancelAll: () => void;
}

export function TransferTrustWarning(p: TrustWarningProps) {
  const { t, locale } = useI18n();
  const lang = locale;
  return (
    <AnimatePresence>
      {p.open && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-slate-950/75 backdrop-blur-sm" data-testid="transfer-trust-warning">
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            className="bg-white dark:bg-[#0d1117] rounded-[2.5rem] border-2 border-amber-500/30 w-full max-w-md shadow-2xl p-6 md:p-8 text-right space-y-6"
            dir={lang === 'ar' ? 'rtl' : 'ltr'}
          >
            <div className="flex flex-col items-center text-center space-y-4">
              <div className="w-16 h-16 rounded-full bg-amber-500/10 text-amber-500 flex items-center justify-center animate-pulse">
                <span className="text-3xl font-extrabold text-amber-500">⚠️</span>
              </div>
              <div className="space-y-2">
                <h3 className="text-xl font-black text-rose-600 dark:text-rose-550">
                  {t('ct.trust_title')}
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 font-bold leading-relaxed px-2">
                  {t('ct.trust_body')}
                </p>
              </div>
            </div>

            {/* Balance Details comparison */}
            <div className="p-4 bg-amber-50/50 dark:bg-amber-950/10 rounded-2xl border border-amber-100/50 dark:border-amber-950/20 space-y-3 font-semibold text-xs text-slate-800 dark:text-slate-200">
              <div className="flex justify-between items-center">
                <span className="text-gray-400">{t('ct.trust_holder')}</span>
                <span className="font-extrabold">{p.clientName}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-400">{t('ct.trust_available')}</span>
                <span className="font-mono font-black text-emerald-600 dark:text-emerald-400">
                  {p.availableLabel}
                </span>
              </div>
              <div className="flex justify-between items-center text-rose-600">
                <span className="text-rose-500">{t('ct.trust_required')}</span>
                <span className="font-mono font-black">
                  {p.requiredLabel}
                </span>
              </div>
            </div>

            <div className="text-center font-bold text-slate-700 dark:text-slate-300 text-xs px-2">
              {t('ct.trust_hint')}
            </div>

            {/* Warning actions */}
            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <button
                type="button"
                onClick={p.onChooseOther}
                className="w-full py-3.5 bg-indigo-600 hover:bg-indigo-500 text-white font-black text-xs rounded-2xl transition-all shadow-md active:scale-95 text-center cursor-pointer"
                data-testid="transfer-trust-choose-other"
              >
                {t('ct.choose_other')}
              </button>
              <button
                type="button"
                onClick={p.onCancelAll}
                className="w-full py-3.5 bg-gray-100 dark:bg-[#161b22] hover:bg-gray-200 dark:hover:bg-[#30363d] text-slate-700 dark:text-[#8b949e] font-black text-xs rounded-2xl transition-all text-center cursor-pointer"
              >
                {t('ct.cancel_all')}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}

export interface OfficeWarningProps {
  readonly open: boolean;
  /** `out`: the partner's debt is smaller than its credit (old dialog, rest as trust); `in`: its trust is smaller than the charge (rest as debt, D-082). */
  readonly mode: 'out' | 'in';
  readonly officeName: string;
  readonly availableLabel: string;
  readonly requiredLabel: string;
  readonly restLabel: string;
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
}

export function TransferOfficeWarning(p: OfficeWarningProps) {
  const { t, locale } = useI18n();
  const lang = locale;
  const inMode = p.mode === 'in';
  return (
    <AnimatePresence>
      {p.open && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-slate-950/75 backdrop-blur-sm" data-testid="transfer-office-warning">
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            className="bg-white dark:bg-[#0d1117] rounded-[2.5rem] border-2 border-indigo-500/30 w-full max-w-md shadow-2xl p-6 md:p-8 text-right space-y-6"
            dir={lang === 'ar' ? 'rtl' : 'ltr'}
          >
            <div className="flex flex-col items-center text-center space-y-4">
              <div className="w-16 h-16 rounded-full bg-indigo-500/10 text-indigo-500 flex items-center justify-center">
                <span className="text-3xl font-extrabold text-indigo-500">🏢</span>
              </div>
              <div className="space-y-2">
                <h3 className="text-xl font-black text-rose-600 dark:text-rose-500">
                  {t('ct.office_title')}
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 font-bold leading-relaxed px-2">
                  {inMode ? t('ct.office_body_in') : t('ct.office_body')}
                </p>
              </div>
            </div>

            {/* Balance Details comparison */}
            <div className="p-4 bg-indigo-50/50 dark:bg-indigo-950/10 rounded-2xl border border-indigo-100/50 dark:border-indigo-950/20 space-y-3 font-semibold text-xs text-slate-800 dark:text-slate-200">
              <div className="flex justify-between items-center">
                <span className="text-gray-400">{t('ct.office_name')}</span>
                <span className="font-extrabold">{p.officeName}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-400">{inMode ? t('ct.office_available_in') : t('ct.office_available')}</span>
                <span className="font-mono font-black text-emerald-600 dark:text-emerald-400">
                  {p.availableLabel}
                </span>
              </div>
              <div className="flex justify-between items-center text-rose-600 font-bold">
                <span className="text-rose-500">{t('ct.office_required')}</span>
                <span className="font-mono font-black animate-pulse">
                  {p.requiredLabel}
                </span>
              </div>
              <div className="pt-2 border-t border-dashed border-gray-200 dark:border-gray-700 flex justify-between items-center text-indigo-650 dark:text-indigo-400 font-extrabold text-[11px]">
                <span>{inMode ? t('ct.office_to_debt') : t('ct.office_to_trust')}</span>
                <span className="font-mono font-black">
                  {p.restLabel}
                </span>
              </div>
            </div>

            <div className="text-center font-bold text-slate-700 dark:text-slate-300 text-xs px-2 leading-relaxed">
              {inMode ? t('ct.office_question_in') : t('ct.office_question')}
            </div>

            {/* Warning actions */}
            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <button
                type="button"
                onClick={p.onConfirm}
                className="w-full py-3.5 bg-emerald-600 hover:bg-emerald-500 text-white font-black text-xs rounded-2xl transition-all shadow-md active:scale-95 text-center cursor-pointer font-bold"
                data-testid="transfer-office-confirm"
              >
                {inMode ? t('ct.office_confirm_in') : t('ct.office_confirm')}
              </button>
              <button
                type="button"
                onClick={p.onCancel}
                className="w-full py-3.5 bg-gray-100 dark:bg-[#161b22] hover:bg-gray-200 dark:hover:bg-[#30363d] text-slate-700 dark:text-[#8b949e] font-black text-xs rounded-2xl transition-all text-center cursor-pointer font-bold"
              >
                {t('ct.cancel_all')}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}

export interface VaultWarningProps {
  readonly open: boolean;
  readonly vaultName: string;
  readonly availableLabel: string;
  readonly requiredLabel: string;
  readonly deficitLabel: string;
  /** Switching to credit / trust exists only while the cash part is the customer's (old buttons). */
  readonly canSwitch: boolean;
  readonly onSwitchCredit: () => void;
  readonly onSwitchTrust: () => void;
  readonly onClose: () => void;
}

export function TransferVaultWarning(p: VaultWarningProps) {
  const { t, locale } = useI18n();
  const lang = locale;
  return (
    <AnimatePresence>
      {p.open && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-slate-950/75 backdrop-blur-sm" data-testid="transfer-vault-warning">
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            className="bg-white dark:bg-[#0d1117] rounded-[2.5rem] border-2 border-rose-500/30 w-full max-w-md shadow-2xl p-6 md:p-8 text-right space-y-6"
            dir={lang === 'ar' ? 'rtl' : 'ltr'}
          >
            <div className="flex flex-col items-center text-center space-y-4">
              <div className="w-16 h-16 rounded-full bg-rose-500/10 text-rose-500 flex items-center justify-center">
                <AlertTriangle size={36} className="text-rose-500" />
              </div>
              <div className="space-y-2">
                <h3 className="text-xl font-black text-rose-600 dark:text-rose-500">
                  {t('ct.vault_title')}
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 font-bold leading-relaxed px-2">
                  {t('ct.vault_body')}
                </p>
              </div>
            </div>

            {/* Balance Details comparison */}
            <div className="p-4 bg-rose-50/50 dark:bg-rose-950/10 rounded-2xl border border-rose-100/50 dark:border-rose-950/20 space-y-3 font-semibold text-xs text-slate-800 dark:text-slate-200">
              <div className="flex justify-between items-center">
                <span className="text-gray-400">{t('ct.vault_target')}</span>
                <span className="font-extrabold">{p.vaultName}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-400">{t('ct.vault_available')}</span>
                <span className="font-mono font-black text-amber-600 dark:text-amber-400">
                  {p.availableLabel}
                </span>
              </div>
              <div className="flex justify-between items-center text-rose-600 font-bold">
                <span className="text-rose-500">{t('ct.vault_required')}</span>
                <span className="font-mono font-black">
                  {p.requiredLabel}
                </span>
              </div>
              <div className="pt-2 border-t border-dashed border-rose-200 dark:border-rose-900 flex justify-between items-center text-rose-600 font-black text-xs">
                <span>{t('ct.vault_deficit')}</span>
                <span className="font-mono">
                  -{p.deficitLabel}
                </span>
              </div>
            </div>

            <div className="text-center font-bold text-slate-700 dark:text-slate-300 text-xs px-2 leading-relaxed">
              {t('ct.vault_hint')}
            </div>

            {/* Warning actions */}
            <div className={`grid grid-cols-1 ${p.canSwitch ? 'sm:grid-cols-3' : ''} gap-2.5 pt-2`}>
              {p.canSwitch && (
                <>
                  <button
                    type="button"
                    onClick={p.onSwitchCredit}
                    className="py-3.5 px-2 bg-indigo-600 hover:bg-indigo-500 text-white font-black text-xs rounded-2xl transition-all shadow-md active:scale-95 text-center cursor-pointer"
                    data-testid="transfer-vault-credit"
                  >
                    {t('ct.switch_credit')}
                  </button>
                  <button
                    type="button"
                    onClick={p.onSwitchTrust}
                    className="py-3.5 px-2 bg-emerald-600 hover:bg-emerald-500 text-white font-black text-xs rounded-2xl transition-all shadow-md active:scale-95 text-center cursor-pointer"
                    data-testid="transfer-vault-trust"
                  >
                    {t('ct.switch_trust')}
                  </button>
                </>
              )}
              <button
                type="button"
                onClick={p.onClose}
                className="py-3.5 px-2 bg-gray-100 dark:bg-[#161b22] hover:bg-gray-200 dark:hover:bg-[#30363d] text-slate-700 dark:text-[#8b949e] font-black text-xs rounded-2xl transition-all text-center cursor-pointer"
                data-testid="transfer-vault-close"
              >
                {t('ct.close_edit')}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}

export interface DuplicateCodeWarningProps {
  readonly open: boolean;
  readonly code: string;
  readonly matches: readonly { readonly numberLabel: string; readonly name: string; readonly dateLabel: string }[];
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
}

/** New (D-087): an incoming code already recorded from the same partner — save only after acknowledging it. */
export function TransferDuplicateCodeWarning(p: DuplicateCodeWarningProps) {
  const { t, locale } = useI18n();
  const lang = locale;
  return (
    <AnimatePresence>
      {p.open && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-slate-950/75 backdrop-blur-sm" data-testid="transfer-duplicate-warning">
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            className="bg-white dark:bg-[#0d1117] rounded-[2.5rem] border-2 border-amber-500/30 w-full max-w-md shadow-2xl p-6 md:p-8 text-right space-y-6"
            dir={lang === 'ar' ? 'rtl' : 'ltr'}
          >
            <div className="flex flex-col items-center text-center space-y-4">
              <div className="w-16 h-16 rounded-full bg-amber-500/10 text-amber-500 flex items-center justify-center animate-pulse">
                <span className="text-3xl font-extrabold text-amber-500">⚠️</span>
              </div>
              <div className="space-y-2">
                <h3 className="text-xl font-black text-rose-600 dark:text-rose-550">{t('ct.dup_title')}</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 font-bold leading-relaxed px-2">{t('ct.dup_body', { code: p.code })}</p>
              </div>
            </div>
            <div className="p-4 bg-amber-50/50 dark:bg-amber-950/10 rounded-2xl border border-amber-100/50 dark:border-amber-950/20 space-y-2 font-semibold text-xs text-slate-800 dark:text-slate-200">
              {p.matches.map((m) => (
                <div key={m.numberLabel} className="flex justify-between items-center">
                  <span className="font-mono font-black text-indigo-600">{m.numberLabel}</span>
                  <span className="font-extrabold">{m.name}</span>
                  <span className="font-mono text-gray-400">{m.dateLabel}</span>
                </div>
              ))}
            </div>
            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <button type="button" onClick={p.onConfirm} className="w-full py-3.5 bg-emerald-600 hover:bg-emerald-500 text-white font-black text-xs rounded-2xl transition-all shadow-md active:scale-95 text-center cursor-pointer" data-testid="transfer-duplicate-confirm">
                {t('ct.dup_confirm')}
              </button>
              <button type="button" onClick={p.onCancel} className="w-full py-3.5 bg-gray-100 dark:bg-[#161b22] hover:bg-gray-200 dark:hover:bg-[#30363d] text-slate-700 dark:text-[#8b949e] font-black text-xs rounded-2xl transition-all text-center cursor-pointer">
                {t('ct.close_edit')}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}

export interface CancelTransferDialogProps {
  readonly open: boolean;
  readonly code: string;
  readonly busy: boolean;
  readonly onConfirm: (reason: string) => void;
  readonly onClose: () => void;
}

/** New (D-086): cancel = one reversal entry with a mandatory written reason; the PIN follows. */
export function CancelTransferDialog(p: CancelTransferDialogProps) {
  const { t, locale } = useI18n();
  const lang = locale;
  const [reason, setReason] = useState('');
  return (
    <AnimatePresence>
      {p.open && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-slate-950/75 backdrop-blur-sm" data-testid="transfer-cancel-dialog">
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            className="bg-white dark:bg-[#0d1117] rounded-[2.5rem] border-2 border-rose-500/30 w-full max-w-md shadow-2xl p-6 md:p-8 text-right space-y-6"
            dir={lang === 'ar' ? 'rtl' : 'ltr'}
          >
            <div className="flex flex-col items-center text-center space-y-4">
              <div className="w-16 h-16 rounded-full bg-rose-500/10 text-rose-500 flex items-center justify-center">
                <AlertTriangle size={36} className="text-rose-500" />
              </div>
              <div className="space-y-2">
                <h3 className="text-xl font-black text-rose-600 dark:text-rose-500">{t('ct.cancel_modal_title')}</h3>
                <p className="text-[11px] font-mono font-black text-indigo-600">{t('ct.code_label')}: {p.code}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 font-bold leading-relaxed px-2">{t('ct.cancel_modal_body')}</p>
              </div>
            </div>
            <div className="space-y-1.5">
              <label className="block font-black text-slate-700 dark:text-slate-300 text-xs">{t('ct.cancel_reason')}</label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
                className="w-full px-4 py-3 bg-white dark:bg-[#05070a] border border-gray-200 dark:border-[#30363d] rounded-xl focus:outline-none focus:ring-2 focus:ring-rose-500 font-extrabold text-slate-800 dark:text-slate-100 text-xs"
                data-testid="transfer-cancel-reason"
              />
            </div>
            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <button
                type="button"
                disabled={p.busy || reason.trim() === ''}
                onClick={() => p.onConfirm(reason.trim())}
                className="w-full py-3.5 bg-rose-600 hover:bg-rose-500 disabled:opacity-50 text-white font-black text-xs rounded-2xl transition-all shadow-md active:scale-95 text-center cursor-pointer"
                data-testid="transfer-cancel-confirm"
              >
                {t('ct.cancel_confirm')}
              </button>
              <button
                type="button"
                onClick={() => {
                  setReason('');
                  p.onClose();
                }}
                className="w-full py-3.5 bg-gray-100 dark:bg-[#161b22] hover:bg-gray-200 dark:hover:bg-[#30363d] text-slate-700 dark:text-[#8b949e] font-black text-xs rounded-2xl transition-all text-center cursor-pointer"
              >
                {t('ct.close')}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
